import React, { useEffect, useState } from "react";

import { Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { Button, ButtonWrapper, BackLink, IconControl } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import Loading from "ds/PageLoading";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { useLoadInvitationsAndAccesses } from "Reducers/access/access.hooks";
import { selectAccessesForSingleUser } from "Reducers/access/environmentTypeAccess/environmentTypeAccess.selectors";
import { createEnvironmentTypeAccessThunk } from "Reducers/access/environmentTypeAccess/thunks/createEnvironmentTypeAccess.thunk";
import { updateEnvironmentTypeAccessThunk } from "Reducers/access/environmentTypeAccess/thunks/updateEnvironmentTypeAccess.thunk";
import { deleteProjectAccessThunk } from "Reducers/access/projectAccess/thunks/deleteProjectAccess.thunk";
import { updateProjectAccessThunk } from "Reducers/access/projectAccess/thunks/updateProjectAccess.thunk";
import { useLoggedInMember } from "Reducers/organization/loggedInMember/hooks/useLoggedInMember";
import { selectHasManagementAccess } from "Reducers/project/project";
import useOrganizationFromURL from "src/organization/pages/settings/pages/members/hooks/useOrganizationFromURL";
import { useAppDispatch } from "Store/hooks";

import EnvironmentTypeRow from "../Common/EnvironmentTypeRow";

import * as S from "./UserDetails.styles";

import type { ConsoleAccessRole } from "@packages/client";

const UserDetails = () => {
  const { projectId = "", organizationId = "" } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const organization = useOrganizationFromURL(organizationId);
  const { load: loadLoggedInMember } = useLoggedInMember(
    organization?.id || ""
  );

  useEffect(() => {
    loadLoggedInMember();
  }, [loadLoggedInMember]);
  const loading = useLoadInvitationsAndAccesses();

  const navigate = useNavigate();
  const manageAccessEnabled = useSelectorWithUrlParams(
    selectHasManagementAccess
  );
  const user = useSelectorWithUrlParams(selectAccessesForSingleUser);

  const isOwner = organization?.owner_id === user?.id;

  const [productionRole, setProductionRole] =
    useState<ConsoleAccessRole | null>(
      user?.environments?.production?.role || null
    );
  const [stagingRole, setStagingRole] = useState<ConsoleAccessRole | null>(
    user?.environments?.staging?.role || null
  );
  const [developmentRole, setDevelopmentRole] =
    useState<ConsoleAccessRole | null>(
      user?.environments?.development?.role || null
    );

  const [isProjectAdmin, setIsProjectAdmin] = useState<boolean>(
    user?.role === "admin"
  );

  // UI State
  const [hasFormBeenEdited, setHasFormBeenEdited] = useState(false);
  const [
    removeUserConfirmationModalIsOpen,
    setRemoveUserConfirmationModalIsOpen
  ] = useState(false);

  const intl = useIntl();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setProductionRole(user?.environments?.production?.role || null);
    setStagingRole(user?.environments?.staging?.role || null);
    setDevelopmentRole(user?.environments?.development?.role || null);
    setIsProjectAdmin(user?.role === "admin");
  }, [user]);

  const accessListURL = `/${organizationId}/${projectId}/-/settings/access`;
  const showProjectPermissions = isProjectAdmin;
  const showEnvironmentPermissions = !showProjectPermissions;

  const handleProjectAdminChecked = (e: boolean) => {
    setIsProjectAdmin(e);
    setHasFormBeenEdited(true);
  };

  const roleHandlers = {
    production: setProductionRole,
    staging: setStagingRole,
    development: setDevelopmentRole
  };

  const roleValues = {
    production: productionRole,
    staging: stagingRole,
    development: developmentRole
  };

  const handleRoleChange = (value, meta) => {
    setHasFormBeenEdited(true);
    roleHandlers[meta.name](value.value);
  };

  const handleRemoveUser = () => {
    setRemoveUserConfirmationModalIsOpen(true);
  };

  const removeUser = async () => {
    if (!user) throw new Error("User not found");

    await dispatch(
      deleteProjectAccessThunk({
        access: user?.projectAccess,
        projectId,
        organizationId,
        id: user?.id
      })
    );
    navigate(accessListURL);
    return;
  };

  const updateProjectAccess = async (access, role) => {
    await dispatch(
      updateProjectAccessThunk({ organizationId, projectId, access, role })
    );
  };

  const handleSave = async () => {
    const projectRole = isProjectAdmin ? "admin" : "viewer";

    if (user) {
      // update project access this must happen before the per environment
      // access updates otherwise the other permissions will fail
      if (user.role !== projectRole) {
        await updateProjectAccess(user.projectAccess, projectRole);
      }

      // don't update env  if user is admin
      if (projectRole === "admin") {
        navigate(accessListURL);
        return;
      }

      // update access for each environments type
      Object.entries(user.environments).forEach(
        ([environmentTypeName, environment]: any) => {
          const roleHasNotChanged =
            environment.role === roleValues[environmentTypeName];

          if (roleHasNotChanged) {
            return;
          }

          const userHasAlreadyAccessToThisEnvType =
            environment.role !== "no-access";

          // 1. If the user has already access to the envType we update it
          if (userHasAlreadyAccessToThisEnvType) {
            dispatch(
              updateEnvironmentTypeAccessThunk({
                projectId,
                environmentTypeId: environmentTypeName,
                accessId: environment.access.id,
                role: roleValues[environmentTypeName]
              })
            );
          } else {
            //  2. if the user has no access yet, we need to add it
            dispatch(
              createEnvironmentTypeAccessThunk({
                projectId,
                environmentTypeId: environmentTypeName,
                user: user.id,
                role: roleValues[environmentTypeName]
              })
            );
          }
        }
      );
    }
    navigate(accessListURL);
  };

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return null;
  }

  return (
    <section>
      <BackLink from="access" to={accessListURL} capitalized={false} />
      <Typography.Title tag="h3">{user.name}</Typography.Title>

      <S.Container>
        <Input
          id="email"
          label={intl.formatMessage({ id: "email" })}
          value={user.email}
          disabled
          aria-disabled
          analyticId="email"
        />
        <>
          <Typography.Title tag="h4" style={{ margin: "16px 0" }}>
            {intl.formatMessage({
              id: "access.project.permissions"
            })}
          </Typography.Title>
          <Checkbox
            label={`${intl.formatMessage({ id: "access.project_admin" })}`}
            checked={isProjectAdmin}
            disabled={!manageAccessEnabled || isOwner}
            aria-disabled={!manageAccessEnabled || isOwner}
            onCheckedChanged={handleProjectAdminChecked}
            forId="super-user"
            id="access.project_admin"
          />
        </>
        {showEnvironmentPermissions && (
          <>
            <S.EnvironmentPermissionsWrapper>
              <Typography.Title tag="h4" style={{ margin: "16px 16px 16px 0" }}>
                {intl.formatMessage({
                  id: "invitation.environment_types.permission"
                })}
              </Typography.Title>
              <Dialog
                align="start"
                description={intl.formatMessage({
                  id: "access.environment_types.description"
                })}
                analyticId="access.environment_types.description"
                link={
                  <a
                    href={intl.formatMessage({
                      id: "links.documentation.users"
                    })}
                  >
                    {intl.formatMessage({ id: "learnmore" })}
                  </a>
                }
                trigger={
                  <IconControl>
                    <S.InfoIcon />
                  </IconControl>
                }
              />
            </S.EnvironmentPermissionsWrapper>
            <S.EnvironmentTypesTable>
              <S.EnvironmentTableHeaders>
                <S.EnvironmentTableHeader width="20%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.environment_types"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
                <S.EnvironmentTableHeader width="35%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.environment.type"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
                <S.EnvironmentTableHeader width="45%">
                  <Typography.Label>
                    {intl.formatMessage({
                      id: "invitation.table.environments"
                    })}
                  </Typography.Label>
                </S.EnvironmentTableHeader>
              </S.EnvironmentTableHeaders>
              {Object.entries(user.environments).map(
                ([environmentType, environment]: any[]) => (
                  <EnvironmentTypeRow
                    key={environmentType}
                    environmentType={environmentType}
                    environment={environment}
                    value={roleValues[environmentType]}
                    onChange={handleRoleChange}
                    disabled={!manageAccessEnabled || isOwner}
                  />
                )
              )}
            </S.EnvironmentTypesTable>
          </>
        )}
        <div
          style={{
            marginTop: "40px",
            display: "flex",
            justifyContent: hasFormBeenEdited ? "space-between" : "flex-end"
          }}
        >
          {hasFormBeenEdited && (
            <ButtonWrapper justifyContent="end">
              <Button onClick={handleSave} analyticId="save">
                {capitalize(intl.formatMessage({ id: "save" }))}
              </Button>
              <Button
                variant="secondary"
                analyticId="cancel"
                to={accessListURL}
              >
                {capitalize(intl.formatMessage({ id: "cancel" }))}
              </Button>
            </ButtonWrapper>
          )}
          {manageAccessEnabled && (
            <ButtonWrapper justifyContent="end">
              {isOwner ? (
                <Tooltip
                  tooltip={intl.formatMessage({
                    id: "project.access.owner.tooltip"
                  })}
                  align="end"
                  analyticId="project.access.owner.tooltip"
                >
                  <Button
                    analyticId="project.access.remove_user"
                    id={`user-remove-${user.id}`}
                    variant="outline"
                    onClick={handleRemoveUser}
                    disabled={true}
                  >
                    {intl.formatMessage({ id: "project.access.remove_user" })}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  id={`user-remove-${user.id}`}
                  variant="outline"
                  onClick={handleRemoveUser}
                  analyticId="project.access.remove_user"
                >
                  {intl.formatMessage({ id: "project.access.remove_user" })}
                </Button>
              )}
            </ButtonWrapper>
          )}
        </div>
      </S.Container>
      <RemoveUserModal
        isOpen={removeUserConfirmationModalIsOpen}
        setIsOpen={setRemoveUserConfirmationModalIsOpen}
        email={user.email}
        onAccept={removeUser}
      />
    </section>
  );
};

function RemoveUserModal({ email, isOpen, setIsOpen, onAccept }) {
  const intl = useIntl();
  const [isDisabled, setIsDisabled] = useState(false);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={() => {
        setIsOpen(false);
      }}
      handleClose={() => {
        setIsOpen(false);
      }}
      id="remove-user-modal"
    >
      <Typography.Title tag="h2">
        {intl.formatMessage({ id: "access.removeUserModalTitle" })}
      </Typography.Title>
      <Typography.BodyText>
        {intl.formatMessage({
          id: "access.removeUserModalBody"
        })}
        {" " + email}
      </Typography.BodyText>
      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button
          variant="outline"
          onClick={() => {
            setIsOpen(false);
          }}
          analyticId="cancel"
        >
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        <Button
          disabled={isDisabled}
          onClick={() => {
            setIsDisabled(true);
            onAccept();
          }}
          analyticId="accept"
        >
          {intl.formatMessage({ id: "accept" })}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
}

export default UserDetails;
